import { Component, computed, ElementRef, inject, model, signal, ViewChild } from '@angular/core';
import { AuthService } from '@/features/auth/auth.service';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AvatarModule } from 'primeng/avatar';

interface NotificationsBars {
    id: string;
    label: string;
    badge?: string | any;
}

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, RippleModule, BadgeModule, OverlayBadgeModule, AvatarModule, FormsModule],
    template: ` <!-- MODAL DE BUSCA -->
        <div *ngIf="isSearchModalOpen" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start pt-20" (click)="closeSearchModal()">
            <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl" (click)="$event.stopPropagation()">
                <div class="p-4">
                    <div class="relative">
                        <input
                            #searchInput
                            type="text"
                            class="w-full py-3 pl-5 pr-12 rounded-full bg-off-white border-2 border-cinza-tecnologia-light-1 focus:outline-none focus:border-azul-confianca-base"
                            placeholder="O que você precisa?"
                            [(ngModel)]="searchTerm"
                            (keyup.enter)="onSearch()"
                        />
                        <button (click)="onSearch()" class="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-200">
                            <svg class="w-6 h-6 text-cinza-tecnologia-base" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="p-6">
                    <h3 class="text-xl font-bold text-cinza-tecnologia-base mb-4 flex items-center">
                        <i class="pi pi-history mr-2"></i>
                        Mais Buscados
                    </h3>
                    <ol class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-cinza-tecnologia-base">
                        <li *ngFor="let item of maisBuscados; let i = index" class="flex items-center py-1">
                            <span class="text-verde-saude-base font-bold mr-3 w-6 text-right">{{ i + 1 }}.</span>
                            <a href="#" class="hover:underline">{{ item }}</a>
                        </li>
                    </ol>
                </div>
            </div>
        </div>

        <div class="layout-topbar bg-white shadow-sm hidden">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full px-4 sm:px-6 py-3 sm:py-4 gap-2 sm:gap-0">
                <!-- Left: Menu button e Logo -->
                <div class="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-0">
                    <a tabindex="0" #menubutton type="button" class="menu-button" (click)="onMenuButtonClick()">
                        <i class="pi pi-chevron-left"></i>
                    </a>
                    <div class="flex flex-col">
                        <h1 class="text-base sm:text-xl font-medium text-slate-900">Olá, Gustavo Vasconcelos</h1>
                        <p class="text-xs sm:text-sm text-slate-600"></p>
                    </div>
                    <!-- Saldo BitLibbs -->
                    <div class="flex items-center gap-1 sm:gap-2">
                        <span class="text-xs sm:text-sm text-slate-600">Saldo BitLibbs</span>
                        <span class="text-sm sm:text-base font-semibold text-green-600">5,000 BL</span>
                    </div>
                </div>

                <!-- Center: Search input -->
                <div class="flex items-center justify-center w-full sm:w-auto mb-2 sm:mb-0">
                    <div class="relative w-full max-w-md" (click)="openSearchModal()">
                        <div
                            class="w-full py-2 sm:py-3 pl-4 sm:pl-5 pr-10 sm:pr-12 rounded-full bg-white shadow-md text-cinza-tecnologia-base cursor-pointer text-center text-base sm:text-lg font-medium transition hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-azul-confianca-base"
                        >
                            O que você precisa?
                        </div>
                        <div class="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-2 pointer-events-none">
                            <svg class="w-5 h-5 sm:w-6 sm:h-6 text-cinza-tecnologia-base" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Right: Seção direita com avatar e informações -->
                <div class="flex items-center gap-4 sm:gap-8 w-full sm:w-auto justify-between sm:justify-end">
                    <!-- Notificações -->
                    <div class="static sm:relative">
                        <button
                            class="relative p-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                            pStyleClass="@next"
                            enterFromClass="hidden"
                            enterActiveClass="animate-scalein"
                            leaveActiveClass="animate-fadeout"
                            leaveToClass="hidden"
                            [hideOnOutsideClick]="true"
                        >
                            <span class="w-2 h-2 rounded-full bg-red-500 absolute top-2 right-2.5"></span>
                            <i class="pi pi-bell text-xl"></i>
                        </button>
                        <div class="hidden absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg w-[22rem] z-50">
                            <div class="p-4 flex items-center justify-between border-b">
                                <span class="text-sm font-medium">Notificações</span>
                                <button class="py-1 px-2 text-sm hover:bg-slate-50 border rounded-lg transition-all">Marcar todas como lidas</button>
                            </div>
                            <div class="flex items-center border-b">
                                @for (item of notificationsBars(); track item.id; let i = $index) {
                                    <button
                                        [ngClass]="{ 'border-slate-900': selectedNotificationBar() === item.id, 'border-transparent': selectedNotificationBar() !== item.id }"
                                        class="px-3.5 py-2 inline-flex items-center border-b gap-2"
                                        (click)="selectedNotificationBar.set(item.id)"
                                    >
                                        <span [ngClass]="{ 'text-slate-900': selectedNotificationBar() === item.id }" class="text-sm">
                                            {{ item.label }}
                                        </span>
                                        <p-badge *ngIf="item?.badge" [value]="item.badge" severity="success" size="small" styleClass="rounded-md" />
                                    </button>
                                }
                            </div>
                            <div class="max-h-80 overflow-auto">
                                @for (item of selectedNotificationsBarData(); track item.name; let i = $index) {
                                    <div class="flex items-center gap-3 px-6 py-3.5 cursor-pointer hover:bg-slate-50 transition-all">
                                        <p-overlay-badge value="" severity="danger">
                                            <p-avatar [image]="item.image" styleClass="w-10 h-10" shape="circle" />
                                        </p-overlay-badge>
                                        <div class="flex flex-col gap-1">
                                            <span class="text-sm font-medium text-slate-900">{{ item.name }}</span>
                                            <span class="text-xs text-slate-600 line-clamp-1">{{ item.description }}</span>
                                            <span class="text-xs text-slate-500">{{ item.time }}</span>
                                        </div>
                                        <p-badge *ngIf="item.new" value="" severity="success" styleClass="ml-auto" />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    <!-- Carrinho -->
                    <button class="p-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors">
                        <i class="pi pi-shopping-cart text-xl"></i>
                    </button>

                    <!-- Avatar e Menu do Usuário -->
                    <div class="static sm:relative">
                        <ng-container *ngIf="auth.isLoggedIn; else loginBtn">
                            <button
                                class="flex items-center space-x-2 p-1 rounded-full hover:bg-slate-50 transition-colors"
                                pStyleClass="@next"
                                enterFromClass="hidden"
                                enterActiveClass="animate-scalein"
                                leaveActiveClass="animate-fadeout"
                                leaveToClass="hidden"
                                [hideOnOutsideClick]="true"
                            >
                                <p-avatar styleClass="w-10 h-10">
                                    <img src="/layout/images/profile.jpg" alt="Avatar do usuário" />
                                </p-avatar>
                                <span class="ml-2">{{ auth.account?.name || auth.account?.username }}</span>
                            </button>
                            <div class="hidden absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                                <ul class="flex flex-col">
                                    <li>
                                        <a class="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer" (click)="auth.logout()">
                                            <i class="pi pi-power-off mr-2"></i>
                                            <span>Sair</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </ng-container>
                        <ng-template #loginBtn>
                            <button class="flex items-center space-x-2 p-1 rounded-full hover:bg-slate-50 transition-colors" (click)="auth.login()">
                                <i class="pi pi-sign-in mr-2"></i>
                                <span>Entrar</span>
                            </button>
                        </ng-template>
                    </div>
                </div>
            </div>
        </div>`
})
export class AppTopbar {
    layoutService = inject(LayoutService);
    auth: AuthService = inject(AuthService);
    isDarkTheme = computed(() => this.layoutService.isDarkTheme());
    @ViewChild('menubutton') menuButton!: ElementRef;

    notificationsBars = signal<NotificationsBars[]>([
        {
            id: 'inbox',
            label: 'Caixa de Entrada',
            badge: '2'
        },
        {
            id: 'general',
            label: 'Geral'
        },
        {
            id: 'archived',
            label: 'Arquivadas'
        }
    ]);

    notifications = signal<any[]>([
        {
            id: 'inbox',
            data: [
                {
                    image: '/demo/images/avatar/avatar-square-m-2.jpg',
                    name: 'Miguel Lima',
                    description: 'Você tem uma nova mensagem da equipe de suporte sobre sua consulta recente.',
                    time: 'Há 1 hora',
                    new: true
                },
                {
                    image: '/demo/images/avatar/avatar-square-f-1.jpg',
                    name: 'Alice Santos',
                    description: 'Seu relatório foi enviado com sucesso e está em análise.',
                    time: 'Há 10 minutos',
                    new: true
                },
                {
                    image: '/demo/images/avatar/avatar-square-f-2.jpg',
                    name: 'Emília Silva',
                    description: 'O prazo do projeto foi atualizado para 30 de setembro. Por favor, verifique os detalhes.',
                    time: 'Ontem às 16:35',
                    new: false
                }
            ]
        },
        {
            id: 'general',
            data: [
                {
                    image: '/demo/images/avatar/avatar-square-f-1.jpg',
                    name: 'Alice Santos',
                    description: 'Lembrete: Sua assinatura irá expirar em 3 dias. Renove agora para evitar interrupção.',
                    time: 'Há 30 minutos',
                    new: true
                },
                {
                    image: '/demo/images/avatar/avatar-square-m-2.jpg',
                    name: 'Miguel Lima',
                    description: 'A manutenção do servidor foi concluída com sucesso. Não é esperada mais nenhuma indisponibilidade.',
                    time: 'Ontem às 14:15',
                    new: false
                }
            ]
        },
        {
            id: 'archived',
            data: [
                {
                    image: '/demo/images/avatar/avatar-square-m-1.jpg',
                    name: 'Lucas Souza',
                    description: 'Seu agendamento com Dr. Anderson foi confirmado para 12 de outubro às 10:00.',
                    time: 'Há 1 semana',
                    new: true
                },
                {
                    image: '/demo/images/avatar/avatar-square-f-2.jpg',
                    name: 'Emília Silva',
                    description: 'O documento que você enviou foi arquivado com sucesso para referência futura.',
                    time: 'Há 2 semanas',
                    new: false
                }
            ]
        }
    ]);

    selectedNotificationBar = model(this.notificationsBars()[0].id ?? 'inbox');
    selectedNotificationsBarData = computed(() => this.notifications().find((f) => f.id === this.selectedNotificationBar())?.data || []);

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    // Search modal state and logic
    isSearchModalOpen = false;
    searchTerm = '';
    maisBuscados: string[] = ['Medicamentos', 'Dermocosméticos', 'Genéricos', 'Ofertas', 'Saúde da Mulher', 'Saúde do Homem', 'Infantil', 'Vitaminas', 'Beleza', 'Cuidados Pessoais'];

    openSearchModal() {
        this.isSearchModalOpen = true;
        setTimeout(() => {
            const input = document.querySelector<HTMLInputElement>('#searchInput');
            if (input) input.focus();
        }, 100);
    }

    closeSearchModal() {
        this.isSearchModalOpen = false;
    }

    onSearch() {
        // Implement search logic here
        this.closeSearchModal();
    }
}
